/**
 * THMV - Calculadora de frete por CEP
 *
 * Substitui o formulário nativo do tema (País + Estado + CEP) por um campo
 * único de CEP, padrão em e-commerce brasileiro.
 *
 * Como o estado (UF) ainda é necessário para a Shopify calcular as faixas de
 * frete corretamente, ele é resolvido automaticamente a partir do CEP usando
 * a API pública ViaCEP. Se a consulta falhar (offline, API fora do ar, CEP
 * não encontrado), o cálculo segue apenas com país + CEP — a Shopify ainda
 * consegue retornar tarifas na maioria dos casos, só com menos precisão.
 *
 * ATENÇÃO: a API de frete da Shopify (/cart/*_shipping_rates.json) calcula
 * sempre com base no CARRINHO ATUAL. Na página de produto, portanto, o
 * resultado reflete o que já está no carrinho — não o produto sendo visto
 * isoladamente. Ver CUSTOMIZACOES.md para detalhes e alternativas.
 */
if (!customElements.get("thmv-shipping-calculator")) {
  customElements.define(
    "thmv-shipping-calculator",
    class ThmvShippingCalculator extends HTMLElement {
      static MAX_ATTEMPTS = 8;
      static POLL_INTERVAL = 2000;
      static STORAGE_KEY = "thmv_shipping_zip";

      constructor() {
        super();
        this.attemptCount = 0;
      }

      connectedCallback() {
        this.form = this.querySelector(".thmv-shipping__form");
        this.input = this.querySelector(".thmv-shipping__input");
        this.submitButton = this.querySelector(".thmv-shipping__submit");
        this.resultsElement = this.querySelector(".thmv-shipping__results");
        this.templateElement = this.querySelector(".thmv-shipping__template");
        this.locationElement = this.querySelector(".thmv-shipping__location");
        this.errorElement = this.querySelector(".thmv-shipping__error");
        this.freeHintElement = this.querySelector(".thmv-shipping__free-hint");
        this.noteElement = this.querySelector("[data-thmv-shipping-note]");

        if (!this.form || !this.input) return;

        this.country = this.dataset.country || "";
        this.variantId = this.dataset.variantId || "";

        this.form.addEventListener("submit", this.onSubmit.bind(this));
        this.input.addEventListener("input", this.onInput.bind(this));

        this.prefillZip();

        if (this.variantId) {
          this.loadCart().then((cart) => this.updateNoteFromCart(cart));
        }
      }

      /**
       * O aviso de "adicione ao carrinho para um cálculo exato" só existe na
       * PDP (variantId presente). Ele começa visível (renderizado direto no
       * Liquid, sem esperar JS) e some quando confirmamos que a variante
       * desta página já está no carrinho — nesse caso o cálculo já reflete
       * o produto real, e o aviso deixaria de ser verdade.
       */
      updateNoteFromCart(cart) {
        if (!this.noteElement || !this.variantId) return;
        const items = (cart && cart.items) || [];
        const variantInCart = items.some(
          (item) => String(item.variant_id) === String(this.variantId),
        );
        this.noteElement.hidden = variantInCart;
      }

      /**
       * Pré-preenche o CEP, na ordem:
       *   1. último CEP usado pelo próprio visitante (localStorage)
       *   2. CEP do endereço padrão, se o cliente estiver logado
       *
       * Não pedimos permissão de geolocalização do navegador: além do prompt
       * ser invasivo logo na entrada, a coordenada do GPS/IP não devolve CEP
       * diretamente (exigiria um serviço de geocodificação reversa) e costuma
       * errar o bairro em áreas urbanas densas.
       */
      prefillZip() {
        let zip = "";
        try {
          zip = window.localStorage.getItem(ThmvShippingCalculator.STORAGE_KEY) || "";
        } catch (error) {
          zip = "";
        }
        if (!zip) zip = this.dataset.defaultZip || "";
        if (!zip) return;

        const digits = zip.replace(/\D/g, "").slice(0, 8);
        if (digits.length !== 8) return;

        this.input.value = `${digits.slice(0, 5)}-${digits.slice(5)}`;
      }

      rememberZip(zip) {
        try {
          window.localStorage.setItem(ThmvShippingCalculator.STORAGE_KEY, zip);
        } catch (error) {
          /* localStorage indisponível (modo privado) — segue sem lembrar */
        }
      }

      /** Máscara de CEP: 00000-000 */
      onInput() {
        const digits = this.input.value.replace(/\D/g, "").slice(0, 8);
        this.input.value =
          digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
      }

      onSubmit(event) {
        event.preventDefault();

        const zip = this.input.value.replace(/\D/g, "");
        if (zip.length !== 8) {
          this.showError("Informe um CEP válido, com 8 dígitos.");
          this.input.focus();
          return;
        }

        this.reset();
        this.startLoading();

        this.loadCart()
          .then((cart) => {
            this.cart = cart;
            this.updateNoteFromCart(cart);
            if (cart && cart.item_count === 0) {
              this.endLoading();
              this.showError("Adicione o produto ao carrinho para calcular o frete.");
              return null;
            }
            return this.lookupAddress(zip).then((address) => {
              this.lastLocation =
                address && address.city && address.uf
                  ? `${address.city} - ${address.uf}`
                  : "";
              return this.tryCountries(
                this.getCountryCandidates(),
                this.input.value,
                address ? address.province : "",
                address ? address.city : "",
              );
            });
          })
          .then((rates) => {
            if (rates === null) return;
            if (rates && rates.length > 0) {
              this.rememberZip(this.input.value);
              this.showResults(rates);
            } else {
              this.showError(
                "Nenhuma opção de entrega encontrada para este CEP.",
              );
            }
          })
          .catch((error) => {
            console.error("[THMV frete] falha ao calcular:", error);
            this.showError(
              "Não foi possível calcular o frete agora. Tente novamente em instantes.",
            );
          });
      }

      /**
       * A API de frete da Shopify calcula sempre sobre o CARRINHO atual — com
       * o carrinho vazio ela falha. Verificamos antes para dar uma mensagem
       * clara em vez de um erro genérico.
       */
      loadCart() {
        return fetch(`${window["routes"]["cart_url"]}.js`, {
          ...webvista.fetchConfig("json", "GET"),
        })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null);
      }

      /**
       * O endpoint da Shopify é sensível ao formato do país: rejeita o nome
       * localizado ("Brasil", 422 "País/região não é compatível") e só aceita
       * o nome em inglês ("Brazil") ou o código ISO ("BR"). Como a loja só
       * atende o Brasil, fixamos "Brazil" como candidato principal, com o
       * código ISO da loja como reserva.
       */
      getCountryCandidates() {
        return ["Brazil", this.dataset.countryCode]
          .map((value) => (value || "").trim())
          .filter(Boolean)
          .filter((value, index, list) => list.indexOf(value) === index);
      }

      tryCountries(countries, zip, province, city) {
        if (!countries.length) {
          return Promise.reject(
            new Error("Nenhum país configurado para o cálculo de frete."),
          );
        }

        const attempt = (index, lastError) => {
          if (index >= countries.length) {
            if (lastError) throw lastError;
            return [];
          }

          this.attemptCount = 0;
          return this.prepareRates({
            zip: zip,
            country: countries[index],
            province: province,
            city: city,
          })
            .then((rates) => {
              if (rates && rates.length > 0) return rates;
              return attempt(index + 1, lastError);
            })
            .catch((error) => {
              console.warn(
                `[THMV frete] país "${countries[index]}" não aceito:`,
                error.message,
              );
              return attempt(index + 1, error);
            });
        };

        return Promise.resolve().then(() => attempt(0, null));
      }

      /**
       * Resolve cidade/UF a partir do CEP via ViaCEP.
       * Nunca rejeita: falha vira `null` para o cálculo seguir sem a UF.
       */
      lookupAddress(zip) {
        return fetch(`https://viacep.com.br/ws/${zip}/json/`)
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => {
            if (!data || data.erro || !data.uf) return null;
            return {
              city: data.localidade || "",
              uf: data.uf,
              province: ThmvShippingCalculator.UF_NAMES[data.uf] || "",
            };
          })
          .catch(() => null);
      }

      /**
       * Dispara o cálculo e devolve as tarifas encontradas (array).
       *
       * `city` é obrigatório aqui: sem ele, o Frenet (transportadora do
       * Scala) devolve `shipping_rates: []` para qualquer CEP, mesmo válido
       * — o checkout de verdade sempre envia a cidade e por isso funciona.
       */
      prepareRates(address) {
        const shipping_address = {
          country: address.country,
          province: address.province,
          zip: address.zip,
          city: address.city,
        };

        return fetch(
          `${window["routes"]["cart_url"]}/prepare_shipping_rates.json`,
          {
            ...webvista.fetchConfig(),
            ...{ body: JSON.stringify({ shipping_address }) },
          },
        )
          .then((response) =>
            response.text().then((text) => ({ response, text })),
          )
          .then(({ response, text }) => {
            if (!response.ok) {
              throw new Error(
                `prepare_shipping_rates ${response.status}: ${text.slice(0, 200)}`,
              );
            }

            let parsed = null;
            if (text && text.trim()) {
              try {
                parsed = JSON.parse(text);
              } catch (error) {
                parsed = null;
              }
            }
            if (parsed !== null && parsed.ok === false) {
              throw new Error(
                `prepare_shipping_rates retornou ok=false: ${text.slice(0, 200)}`,
              );
            }
            return this.asyncRates({ shipping_address });
          });
      }

      /**
       * A Shopify calcula as tarifas de forma assíncrona (o app transportador
       * — Frenet, no caso desta loja — é consultado em background), então é
       * preciso pesquisar o resultado algumas vezes até ele ficar pronto.
       */
      asyncRates(params) {
        return fetch(
          `${window["routes"]["cart_url"]}/async_shipping_rates.json?` +
            this.serialize(params),
          { ...webvista.fetchConfig("json", "GET") },
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(`async_shipping_rates ${response.status}`);
            }
            return response.json();
          })
          .then((response) => {
            const rates = response && response["shipping_rates"];
            if (rates && rates.length > 0) return rates;

            if (++this.attemptCount >= ThmvShippingCalculator.MAX_ATTEMPTS) {
              return [];
            }

            return new Promise((resolve) => {
              setTimeout(
                () => resolve(this.asyncRates(params)),
                ThmvShippingCalculator.POLL_INTERVAL,
              );
            });
          });
      }

      showResults(rates) {
        this.endLoading();
        if (!this.resultsElement || !this.templateElement) return;

        /**
         * Cidade/UF só aparece junto com as opções de entrega, no mesmo
         * instante — nunca antes. A consulta ViaCEP costuma resolver bem
         * antes da tarifa (que depende do transportador), e mostrar a
         * localização primeiro e a lista de opções alguns instantes depois
         * lia como duas informações soltas, em vez de um resultado só.
         */
        if (this.locationElement && this.lastLocation) {
          this.locationElement.textContent = `Opções de entrega para ${this.lastLocation}`;
          this.locationElement.hidden = false;
        }

        const discountApplies = this.qualifiesForFreeShippingDiscount();

        const template = this.templateElement.content;
        const fragment = document.createDocumentFragment();

        rates.forEach((rate) => {
          const clone = document.importNode(template, true);

          const nameElement = clone.querySelector(".thmv-shipping__result-name");
          if (nameElement) {
            nameElement.textContent =
              rate["presentment-name"] || rate["name"] || "";
          }

          const priceValue = parseFloat(rate["price"]);
          const priceValueElement = clone.querySelector(
            ".thmv-shipping__result-price-value",
          );
          const freeBadgeElement = clone.querySelector(
            ".thmv-shipping__result-price-free",
          );

          if (priceValueElement) {
            priceValueElement.textContent = this.formatPrice(rate["price"]);
          }

          /**
           * O frete grátis por DESCONTO (ver renderFreeShippingNote) não
           * aparece na tarifa em si — a API sempre devolve o preço cheio do
           * transportador. Quando o carrinho já se qualifica, mostramos o
           * valor da tarifa riscado + selo "Grátis" ao lado, igual ao
           * tratamento de preço promocional usado no resto da loja
           * (thmv-product-old-price, card-old-price), em vez de só um
           * texto avisando por fora da lista.
           */
          if (discountApplies && priceValue > 0) {
            if (priceValueElement) {
              priceValueElement.classList.add(
                "thmv-shipping__result-price-value--discounted",
              );
            }
            if (freeBadgeElement) freeBadgeElement.hidden = false;
          }

          const dateElement = clone.querySelector(".thmv-shipping__result-date");
          if (dateElement) {
            const label = this.formatDeliveryDate(rate);
            if (label) {
              dateElement.textContent = label;
            } else {
              dateElement.remove();
            }
          }

          fragment.appendChild(clone);
        });

        this.resultsElement.innerHTML = "";
        this.resultsElement.appendChild(fragment);
        this.resultsElement.hidden = false;

        this.renderFreeShippingNote(discountApplies, rates);
      }

      /**
       * Monta o texto de prazo já no formato brasileiro (DD/MM/AAAA).
       * A Shopify devolve datas em ISO (AAAA-MM-DD).
       */
      formatDeliveryDate(rate) {
        const range = rate["delivery_range"];
        let start = rate["delivery_date"];
        let end = null;

        if (Array.isArray(range) && range.length > 0) {
          start = range[0];
          if (range.length > 1 && range[1] !== range[0]) end = range[1];
        }

        const startLabel = this.toBrDate(start);
        if (!startLabel) return "";

        const endLabel = this.toBrDate(end);
        return endLabel
          ? `Entrega entre ${startLabel} e ${endLabel}`
          : `Entrega até ${startLabel}`;
      }

      toBrDate(value) {
        if (!value || typeof value !== "string") return "";
        const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) return "";
        return `${match[3]}/${match[2]}/${match[1]}`;
      }

      /** Frete zerado aparece como "Grátis" (mais claro que "R$ 0,00"). */
      formatPrice(price) {
        if (parseFloat(price) === 0) return "Grátis";
        const template = window["priceFormatTemplate"];
        if (!template) return price;
        return template.replace(/0([,.]0{0,2})?/, price);
      }

      /**
       * Existem duas formas de configurar frete grátis nesta loja, e elas se
       * comportam de forma diferente aqui:
       *
       * 1. Como TARIFA de envio (Configurações → Envio → faixa "Frete grátis"
       *    com condição de preço). Nesse caso a própria consulta já devolve
       *    uma tarifa de valor zero, e ela aparece na lista como "Grátis" —
       *    sem precisar de nenhum tratamento visual extra.
       *
       * 2. Como DESCONTO automático (Descontos → frete grátis acima de X).
       *    Descontos NÃO entram na consulta de tarifas — ela devolve o preço
       *    cheio do transportador (Frenet, aqui), e o abatimento só acontece
       *    no checkout. Por isso, quando o carrinho já atingiu o limite mas
       *    nenhuma tarifa zerada voltou, mostramos o preço de cada tarifa
       *    riscado com um selo "Grátis" ao lado (ver showResults) — não dá
       *    pra saber se o desconto vale para todas as opções, então aplicamos
       *    a todas por igual, e o texto abaixo esclarece que o abatimento
       *    real acontece no checkout.
       */
      qualifiesForFreeShippingDiscount() {
        const threshold = parseFloat(this.dataset.freeThreshold);
        if (!threshold || !this.cart) return false;

        const rate =
          (window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
        const thresholdCents = Math.round(threshold * rate) * 100;
        return this.cart.total_price >= thresholdCents;
      }

      renderFreeShippingNote(discountApplies, rates) {
        if (!this.freeHintElement) return;

        const hasFreeRate = (rates || []).some(
          (rate) => parseFloat(rate["price"]) === 0,
        );
        if (hasFreeRate) return;

        if (discountApplies) {
          this.showFreeHint("Frete grátis aplicado no checkout.");
          return;
        }

        const threshold = parseFloat(this.dataset.freeThreshold);
        if (!threshold || !this.cart) return;

        const rate =
          (window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
        const thresholdCents = Math.round(threshold * rate) * 100;
        const missing = thresholdCents - this.cart.total_price;
        if (missing <= 0) return;

        this.showFreeHint(
          `Faltam ${this.formatPrice(
            (missing / 100).toFixed(2),
          )} para frete grátis.`,
        );
      }

      showFreeHint(text) {
        if (!this.freeHintElement) return;
        this.freeHintElement.textContent = text;
        this.freeHintElement.hidden = false;
      }

      showError(message) {
        this.endLoading();
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      reset() {
        if (this.resultsElement) {
          this.resultsElement.innerHTML = "";
          this.resultsElement.hidden = true;
        }
        if (this.locationElement) this.locationElement.hidden = true;
        if (this.errorElement) this.errorElement.hidden = true;
        if (this.freeHintElement) this.freeHintElement.hidden = true;
      }

      startLoading() {
        if (!this.submitButton) return;
        this.submitButton.classList.add("loading");
        this.submitButton.setAttribute("disabled", "disabled");
      }

      endLoading() {
        if (!this.submitButton) return;
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("disabled");
      }

      serialize(obj, prefix) {
        return Object.keys(obj)
          .filter((key) => Object.prototype.hasOwnProperty.call(obj, key))
          .map((key) => {
            const k = prefix ? `${prefix}[${key}]` : key;
            const v = obj[key];
            return v !== null && typeof v === "object"
              ? this.serialize(v, k)
              : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          })
          .join("&");
      }

      /**
       * A Shopify espera o nome do estado por extenso (não a sigla) no campo
       * `province`. Mapeamento fixo das 27 unidades federativas.
       */
      static UF_NAMES = {
        AC: "Acre",
        AL: "Alagoas",
        AP: "Amapá",
        AM: "Amazonas",
        BA: "Bahia",
        CE: "Ceará",
        DF: "Distrito Federal",
        ES: "Espírito Santo",
        GO: "Goiás",
        MA: "Maranhão",
        MT: "Mato Grosso",
        MS: "Mato Grosso do Sul",
        MG: "Minas Gerais",
        PA: "Pará",
        PB: "Paraíba",
        PR: "Paraná",
        PE: "Pernambuco",
        PI: "Piauí",
        RJ: "Rio de Janeiro",
        RN: "Rio Grande do Norte",
        RS: "Rio Grande do Sul",
        RO: "Rondônia",
        RR: "Roraima",
        SC: "Santa Catarina",
        SP: "São Paulo",
        SE: "Sergipe",
        TO: "Tocantins",
      };
    },
  );
}
